import * as React from "react"
import H2 from "../../../atoms/typography/H2"
import { HorizontalSpacer } from "../../../atoms/spacers"
import H1 from "../../../atoms/typography/H1"
import H3 from "../../../atoms/typography/H3"
import styled from "styled-components"
import { Col, Row } from "react-grid-system"
import TextInput from "../../TextInput"
import SmallColorfulButton from "../../../atoms/buttons/SmallColorfulButton"
import SecondaryButton from "../../../atoms/buttons/SecondaryButton"
import { useSignUpForMotion } from "../../../../hooks/useSignUpForMotion"
import { ApolloError } from "@apollo/client"
import * as Sentry from "@sentry/react"
import LoadingSpinner from "../../../atoms/LoadingSpinner"
import { checkEmailValid } from "../../../../utils/helpers"
import { pushFormSubmitEvent, FORM_NAME_MOTION_SIGNUP } from "../../../../utils/datalayer"

const ColoredTitle = styled(H1)`
  background: ${(props) => props.theme.gradients.red};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
`

interface Props {
  onClose: () => void
}

interface FormType {
  email: string
  firstName?: string
  lastName?: string
  company?: string
  phone?: string
}

const initialValues: FormType = {
  email: "",
}

const SubscribeToUpdatesModalContent: React.FC<Props> = ({ onClose }) => {
  const [formSent, setFormSent] = React.useState(false)
  const [formValues, setFormValues] = React.useState<FormType>(initialValues)

  const onCompleted = () => {
    setFormSent(true)
  }
  const onError = (error: ApolloError) => Sentry.captureException(error)
  const { signUpForMotion, data, loading } = useSignUpForMotion({ onCompleted, onError })

  const handleChangeForm = (event: any) => {
    const name = event.target.name
    const value = event.target.value

    setFormValues((values: any) => ({ ...values, [name]: value }))
  }

  const onSignUp = async () => {
    if (formValues) {
      try {
        await signUpForMotion({
          variables: {
            email: formValues.email,
            company: formValues.company,
            phone: formValues.phone,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
          },
        })
        pushFormSubmitEvent(FORM_NAME_MOTION_SIGNUP)
      } catch (error) {
        Sentry.captureException(error)
      }
    }
  }

  const onSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    console.log("submit", formValues)
    onSignUp()
  }

  return (
    <div>
      {formSent ? (
        <div>
          <H2 textAlign="center">Thank you for signing up!</H2>
          <HorizontalSpacer spacing={2} />
          <H3 textAlign="center">You will now receive emails with updates regarding new Satcube products.</H3>
          <HorizontalSpacer spacing={2} />
          <Row justify="center">
            <SmallColorfulButton title="Go back to Motion" onClick={onClose} variant="red" />
          </Row>
        </div>
      ) : (
        <div>
          <H2>Sign up for notifications</H2>
          <HorizontalSpacer spacing={0.5} />
          <ColoredTitle>Satcube Motion</ColoredTitle>
          <HorizontalSpacer spacing={2} />
          <H3>
            Stay informed about the development and future release of the smallest and most advanced mobility terminal
            on the market. Fill out the form below to receive the latest product updates and have the possibility to be
            among the first to test the Satcube Motion out!
          </H3>
          <HorizontalSpacer spacing={2} />
          <form onSubmit={onSubmitForm}>
            <Row>
              <Col lg={6}>
                <TextInput
                  textFieldTitle="First name"
                  tabIndex={0}
                  placeholder="First name"
                  name="firstName"
                  id="firstName"
                  onChange={handleChangeForm}
                  autofocus
                />
              </Col>
              <Col lg={6}>
                <TextInput
                  textFieldTitle="Last name"
                  tabIndex={1}
                  placeholder="Last name"
                  name="lastName"
                  id="lastName"
                  onChange={handleChangeForm}
                />
              </Col>
            </Row>
            <HorizontalSpacer spacing={1} />
            <Row>
              <Col lg={6}>
                <TextInput
                  textFieldTitle="Email"
                  tabIndex={2}
                  placeholder="example@example.com"
                  name="email"
                  id="email"
                  onChange={handleChangeForm}
                  required
                />
              </Col>
              <Col lg={6}>
                <TextInput
                  textFieldTitle="Phone"
                  tabIndex={3}
                  placeholder="+ 00 xxxxxxxxx"
                  name="phone"
                  id="phone"
                  onChange={handleChangeForm}
                />
              </Col>
            </Row>
            <HorizontalSpacer spacing={1} />
            <Row>
              <Col lg={12}>
                <TextInput
                  textFieldTitle="Company"
                  tabIndex={4}
                  placeholder="Company name"
                  name="company"
                  id="company"
                  onChange={handleChangeForm}
                />
              </Col>
            </Row>

            <HorizontalSpacer spacing={2} />
            {loading ? (
              <LoadingSpinner />
            ) : (
              <SecondaryButton title="Sign up" type="submit" disabled={checkEmailValid(formValues.email) !== true} />
            )}
          </form>
          <HorizontalSpacer spacing={1} />
        </div>
      )}
    </div>
  )
}

export default SubscribeToUpdatesModalContent
